import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeftIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';

interface DiscordIntegration {
  id: string;
  userId: string;
  discordUserId: string;
  username: string;
  discriminator: string;
  avatar?: string;
  guildId?: string;
  webhookUrl?: string;
  roleMapping?: Record<string, string>;
  accessToken: string;
  refreshToken: string;
  expiresAt: Date;
}

interface Guild {
  id: string;
  name: string;
  icon?: string;
  owner: boolean;
  permissions: number;
}

const DiscordIntegrationPage: React.FC = () => {
  const navigate = useNavigate();
  const [integration, setIntegration] = useState<DiscordIntegration | null>(null);
  const [guilds, setGuilds] = useState<Guild[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    fetchIntegration();
  }, []);

  const fetchIntegration = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/discord-enhanced/status');
      setIntegration(response.data.integration);
      
      if (response.data.integration) {
        fetchGuilds();
      }
    } catch (error: any) {
      if (error.response?.status !== 404) {
        console.error('Error fetching Discord integration:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchGuilds = async () => {
    try {
      const response = await axios.get('/api/discord-enhanced/guilds');
      setGuilds(response.data.guilds);
    } catch (error) {
      console.error('Error fetching guilds:', error);
    }
  };

  const connectDiscord = async () => {
    try {
      const response = await axios.get('/api/discord-enhanced/auth-url');
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error connecting Discord:', error);
    }
  };

  const disconnectDiscord = async () => {
    if (!confirm('Are you sure you want to disconnect Discord?')) return;

    try {
      await axios.delete('/api/discord-enhanced/disconnect');
      setIntegration(null);
      setGuilds([]);
    } catch (error) {
      console.error('Error disconnecting Discord:', error);
    }
  };

  const syncRoles = async () => {
    try {
      setSyncing(true);
      await axios.post(`/api/discord-enhanced/sync-roles/${integration?.userId}`);
      alert('Roles synced successfully!');
    } catch (error) {
      console.error('Error syncing roles:', error);
      alert('Failed to sync roles');
    } finally {
      setSyncing(false);
    }
  };

  const testWebhook = async () => {
    try {
      setTesting(true);
      await axios.post('/api/discord-enhanced/test-webhook', {
        webhookUrl: integration?.webhookUrl,
      });
      alert('Test message sent!');
    } catch (error) {
      console.error('Error testing webhook:', error);
      alert('Failed to send test message');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Back Button */}
      <button
        onClick={() => navigate('/dashboard/integrations')}
        className="flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-6"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        Back to Integrations
      </button>

      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <span className="text-5xl">🎮</span>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Discord Integration</h1>
          <p className="text-gray-600 mt-1">
            Sync roles and send notifications to your Discord server
          </p>
        </div>
      </div>

      {!integration ? (
        /* Not Connected State */
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Connect Your Discord</h2>
          <p className="text-gray-600 mb-6">
            Grant access to your Discord account to enable role synchronization and notifications.
          </p>

          <div className="space-y-4 mb-8">
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-gray-900">Automatic Role Sync</h3>
                <p className="text-sm text-gray-600">
                  Patrons get Discord roles based on their membership tier
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-gray-900">Webhook Notifications</h3>
                <p className="text-sm text-gray-600">
                  Send new post notifications to Discord channels
                </p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <CheckCircleIcon className="h-6 w-6 text-green-500 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-gray-900">Member Management</h3>
                <p className="text-sm text-gray-600">
                  Add or remove members from your Discord server
                </p>
              </div>
            </div>
          </div>

          <button
            onClick={connectDiscord}
            className="w-full sm:w-auto px-6 py-3 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Connect Discord Account
          </button>
        </div>
      ) : (
        /* Connected State */
        <div className="space-y-6">
          {/* Connection Status */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Connection Status</h2>
              <div className="flex items-center gap-2 text-green-600">
                <CheckCircleIcon className="h-5 w-5" />
                <span className="text-sm font-medium">Connected</span>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {integration.avatar && (
                <img
                  src={`https://cdn.discordapp.com/avatars/${integration.discordUserId}/${integration.avatar}.png`}
                  alt="Discord Avatar"
                  className="h-12 w-12 rounded-full"
                />
              )}
              <div>
                <p className="font-medium text-gray-900">
                  {integration.username}#{integration.discriminator}
                </p>
                <p className="text-sm text-gray-500">Discord User ID: {integration.discordUserId}</p>
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={syncRoles}
                disabled={syncing}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <ArrowPathIcon className={`h-4 w-4 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Syncing...' : 'Sync Roles'}
              </button>
              <button
                onClick={disconnectDiscord}
                className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700"
              >
                Disconnect
              </button>
            </div>
          </div>

          {/* Guilds */}
          {guilds.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Your Discord Servers</h2>
              <div className="space-y-3">
                {guilds.map((guild) => (
                  <div
                    key={guild.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      {guild.icon ? (
                        <img
                          src={`https://cdn.discordapp.com/icons/${guild.id}/${guild.icon}.png`}
                          alt={guild.name}
                          className="h-10 w-10 rounded-full"
                        />
                      ) : (
                        <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center text-gray-600 font-semibold">
                          {guild.name.charAt(0)}
                        </div>
                      )}
                      <div>
                        <p className="font-medium text-gray-900">{guild.name}</p>
                        {guild.owner && (
                          <span className="text-xs text-gray-500">Owner</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Webhook Settings */}
          {integration.webhookUrl && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Webhook Notifications</h2>
              <p className="text-sm text-gray-600 mb-4">
                Webhook URL configured. Test the connection to verify it's working.
              </p>
              <button
                onClick={testWebhook}
                disabled={testing}
                className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {testing ? 'Sending...' : 'Send Test Message'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DiscordIntegrationPage;
